/**
 * Recognize an MCP tool call that is an exact stand-in for the native `Write`
 * tool: it names one file and carries the bytes it put there.
 *
 * A native `Write` row gets the file-preview chip on its path and a diff card
 * for its content. An MCP `write_file` call is the same event with a
 * server-authored argument shape, so it should read the same way — but the row
 * can only take the preview chip when nothing else on the call would be hidden
 * by doing so, and a write's `content` is exactly the kind of argument that
 * would be. So the bar here is: the input is a path plus its content and
 * *nothing else*, which is what lets the caller render the content as the diff
 * a native write shows and hand the path the chip.
 *
 * Under-detecting is the safe direction: an unrecognized call keeps the
 * full-arguments tooltip, which shows everything verbatim.
 */

import { PATH_ARGUMENTS } from './tool-path-arguments.js';

/** One file and the bytes a call wrote to it. */
export interface WriteFileInput {
  path: string;
  content: string;
}

/** Keys naming the written bytes. `contents` is a spelling some servers use. */
const CONTENT_KEYS = ['content', 'contents'] as const;

/** The first of `keys` present on `input` as a string, with its value. */
function pick(
  input: Record<string, unknown>,
  keys: readonly string[],
): { key: string; value: string } | null {
  for (const key of keys) {
    const value = input[key];
    if (typeof value === 'string') return { key, value };
  }
  return null;
}

/**
 * Read a `{ path, content }` write payload off a tool call's arguments, or
 * null when the call is anything more than that.
 *
 * An empty `content` is a real write (truncating a file), so only the value's
 * type is checked, not its emptiness. Any third key disqualifies the call: it
 * would become unreachable on a row that traded its argument tooltip for a
 * path chip.
 */
export function writeFileInput(
  input: Record<string, unknown> | null | undefined,
): WriteFileInput | null {
  if (!input || Object.keys(input).length !== 2) return null;
  const path = pick(input, PATH_ARGUMENTS);
  const content = pick(input, CONTENT_KEYS);
  if (!path?.value || !content) return null;
  return { path: path.value, content: content.value };
}
